import MP4Box from 'mp4box';

// Reads capture metadata (duration, resolution, frame rate, codec, bitrate) from a user-selected
// video File WITHOUT uploading, decoding, or converting it — purely by parsing the container's box
// structure with mp4box. Parsing reads headers only, so it works for HEVC / 8K .insv files that the
// browser could never actually play. Returns { ok, ... }; ok:false means metadata couldn't be read
// (e.g. an .osv or an odd container) and the caller should degrade gracefully, not block the upload.

const CHUNK_BYTES = 1024 * 1024;   // 1 MB feed chunks

// Display rotation (0/90/180/270) from the track's ISO-BMFF transformation matrix (tkhd). a=m[0],
// b=m[1] are 16.16 fixed-point; the rotation is atan2(b, a). Phone portrait videos store a landscape
// frame + a 90/270 rotation here. Returns 0 when the matrix is absent or identity.
function rotationFromMatrix(matrix) {
  if (!matrix || matrix.length < 2) return 0;
  const a = matrix[0] / 65536;
  const b = matrix[1] / 65536;
  let deg = Math.round(Math.atan2(b, a) * 180 / Math.PI);
  deg = ((Math.round(deg / 90) * 90) % 360 + 360) % 360;
  return deg;
}

// Feed mp4box chunk by chunk, always reading from the offset it asks for next (appendBuffer's return
// value). That lets it jump over the large mdat and land directly on a moov at the END of the file —
// so metadata reads for both faststart files (moov at front, e.g. iPhone .mp4) and moov-at-end files
// (e.g. Insta360 .insv) without loading the whole (possibly multi-GB) file into memory.
async function parseWithMp4Box(file) {
  const mp4 = MP4Box.createFile();

  let info = null;
  let done = false;
  const ready = new Promise((resolve) => {
    mp4.onReady = (i) => { info = i; done = true; resolve(i); };
    mp4.onError = () => { done = true; resolve(null); };
  });

  const pump = (async () => {
    let offset = 0;
    while (!done && offset < file.size) {
      const end = Math.min(offset + CHUNK_BYTES, file.size);
      const buf = await file.slice(offset, end).arrayBuffer();
      if (done) break;
      buf.fileStart = offset;
      const next = mp4.appendBuffer(buf);   // returns the next byte offset mp4box wants to read
      offset = (typeof next === 'number' && next > end) ? next : end;   // jump past mdat when asked
    }
    try { mp4.flush(); } catch { /* ignore */ }
    return null;
  })();

  await Promise.race([ready, pump]);
  done = true;
  try { mp4.stop(); } catch { /* best-effort */ }
  if (!info) return null;

  const tracks = info.videoTracks || [];
  const track = tracks[0] || null;
  const durationSec = info.duration && info.timescale ? info.duration / info.timescale : 0;

  const dimOf = (t) => ({
    w: (t.video && t.video.width) || t.track_width || 0,
    h: (t.video && t.video.height) || t.track_height || 0,
  });

  let width = 0, height = 0, fps = 0, codec = null;
  if (track) {
    const d0 = dimOf(track);
    width = d0.w;
    height = d0.h;

    // Dual-fisheye 360 (e.g. Insta360 .insv) stores one SQUARE video track per lens; the stitched
    // equirect is the lens frames side by side (~2W × H). Report that combined size rather than a
    // single lens, so a 2×1920² capture reads as 3840×1920, not 1920×1920.
    if (tracks.length >= 2) {
      const dims = tracks.map(dimOf);
      const allSquare = dims.every(d => d.w > 0 && d.h > 0 && Math.abs(d.w - d.h) <= d.w * 0.1);
      if (allSquare) {
        width = dims.reduce((sum, d) => sum + d.w, 0);
        height = Math.max(...dims.map(d => d.h));
      }
    }

    codec = track.codec || null;
    const trackDur = track.duration && track.timescale ? track.duration / track.timescale : durationSec;
    if (trackDur > 0 && track.nb_samples) fps = track.nb_samples / trackDur;
  }

  return {
    ok: true,
    durationSec,
    width,
    height,
    fps,
    codec,
    rotation: track ? rotationFromMatrix(track.matrix) : 0,
    bitrateMbps: durationSec > 0 ? (file.size * 8 / durationSec) / 1e6 : 0,
    sizeBytes: file.size,
    videoTrackCount: (info.videoTracks || []).length,
  };
}

// Fallback for files mp4box can't parse but the browser can still inspect (plain 2D mp4/mov): a
// hidden <video> gives duration + dimensions (not frame rate — the HTML5 API doesn't expose it).
function parseWithVideoElement(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    const cleanup = () => { URL.revokeObjectURL(url); video.removeAttribute('src'); video.load?.(); };
    video.onloadedmetadata = () => {
      const durationSec = isFinite(video.duration) ? video.duration : 0;
      const width = video.videoWidth || 0;
      const height = video.videoHeight || 0;
      cleanup();
      if (!width && !durationSec) { resolve(null); return; }
      resolve({
        ok: true,
        durationSec,
        width,
        height,
        fps: 0,            // not available from the HTML5 video API
        codec: null,
        bitrateMbps: durationSec > 0 ? (file.size * 8 / durationSec) / 1e6 : 0,
        sizeBytes: file.size,
        videoTrackCount: width ? 1 : 0,
      });
    };
    video.onerror = () => { cleanup(); resolve(null); };
    video.src = url;
  });
}

export async function readVideoMetadata(file) {
  if (!file) return { ok: false, sizeBytes: 0 };
  try {
    const fromBox = await parseWithMp4Box(file).catch(() => null);
    if (fromBox && fromBox.ok) return fromBox;

    const fromVideo = await parseWithVideoElement(file).catch(() => null);
    if (fromVideo && fromVideo.ok) return fromVideo;
  } catch { /* fall through */ }
  return { ok: false, sizeBytes: file.size };
}
